import { useState, useEffect } from "react";
import { ref, onValue, push, remove, update } from "firebase/database";
import { getAuth } from "firebase/auth";
import { db as database } from "../Firebase/firestore";
import { set } from "firebase/database"; // add at top
import { Pencil, Trash2 } from "lucide-react";
import "./broadcast.css";

const BroadcastingPage = () => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [activeTab, setActiveTab] = useState("compose");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Form states
  const [heading, setHeading] = useState("");
  const [message, setMessage] = useState("");
  const [validity, setValidity] = useState("2 days");
  const [recipientType, setRecipientType] = useState("all");
  const [editingDraftId, setEditingDraftId] = useState(null);

  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid || "";

  useEffect(() => {
    if (!auth.currentUser) {
      showNotification("Please sign in to use broadcasting", "error");
    }
  }, []);

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(
      () => setNotification({ show: false, message: "", type: "" }),
      4000
    );
  };

  // Fetch Users
  useEffect(() => {
    const profileRef = ref(database, "profileDashboard");
    const unsubscribe = onValue(profileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const userList = Object.keys(data).map((userId) => ({
          userId,
          company_name: data[userId].company_name || "Unknown Company",
        }));
        const sorted = userList.sort((a, b) =>
          a.company_name.localeCompare(b.company_name)
        );
        setUsers(sorted);
      } else {
        setUsers([]);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch Broadcasts
  useEffect(() => {
    if (!currentUserId) return;
    const broadcastRef = ref(database, `broadcasts/${currentUserId}`);
    const unsubscribe = onValue(broadcastRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data).map((id) => ({ id, ...data[id] }));
        setBroadcasts(list);
      } else {
        setBroadcasts([]);
      }
    });
    return () => unsubscribe();
  }, [currentUserId]);

  // Auto-delete expired broadcasts
  useEffect(() => {
    const checkExpiry = () => {
      const now = Date.now();
      broadcasts.forEach(async (b) => {
        if (b.status !== "sent") return;
        const sentTime = new Date(b.timestamp).getTime();
        const validityMs =
          {
            "1 day": 86400000,
            "2 days": 172800000,
            "1 week": 604800000,
            "2 weeks": 1209600000,
            "1 month": 2592000000,
          }[b.validity] || 172800000;

        if (sentTime + validityMs < now) {
          // Delete from sender's broadcasts
          await remove(ref(database, `broadcasts/${currentUserId}/${b.id}`));
          // Delete from all recipients' broadcasts
          if (b.recipients && b.recipients.length > 0) {
            await Promise.all(
              b.recipients.map((rid) =>
                remove(ref(database, `broadcasts/${rid}/${b.id}`))
              )
            );
          }
        }
      });
    };

    const interval = setInterval(checkExpiry, 60000); // Check every minute
    checkExpiry(); // Initial check
    return () => clearInterval(interval);
  }, [broadcasts, currentUserId]);

  // Manual Delete
  const handleDelete = async (id, type) => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    try {
      const broadcast = broadcasts.find((b) => b.id === id);

      // Delete from sender
      await remove(ref(database, `broadcasts/${currentUserId}/${id}`));

      // Delete from all recipients if exists
      if (broadcast?.recipients && broadcast.recipients.length > 0) {
        await Promise.all(
          broadcast.recipients.map((rid) =>
            remove(ref(database, `broadcasts/${rid}/${id}`))
          )
        );
      }

      showNotification(`${type} deleted successfully`);
    } catch (err) {
      console.error("Delete error:", err);
      showNotification("Delete failed", "error");
    }
  };

  // Edit Draft
  const handleEditDraft = (draft) => {
    setHeading(draft.heading);
    setMessage(draft.message);
    setValidity(draft.validity);
    setRecipientType(
      draft.recipients.length === users.length ? "all" : "selected"
    );
    setSelectedUsers(draft.recipients || []);
    setEditingDraftId(draft.id);
    setActiveTab("compose");
    showNotification("Editing draft...");
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const validateForm = () => {
    if (!heading.trim() || !message.trim()) {
      showNotification("Heading and message are required", "error");
      return false;
    }
    if (recipientType === "selected" && selectedUsers.length === 0) {
      showNotification("Select at least one recipient", "error");
      return false;
    }
    return true;
  };

  const getRecipients = () =>
    recipientType === "all" ? users.map((u) => u.userId) : selectedUsers;

  const saveBroadcast = async (status) => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const recipients = getRecipients();
      const data = {
        heading,
        message,
        validity,
        senderId: currentUserId,
        timestamp: new Date().toISOString(),
        status,
        recipients,
      };

      if (editingDraftId && status === "sent") {
        await update(
          ref(database, `broadcasts/${currentUserId}/${editingDraftId}`),
          { ...data, status: "sent" }
        );
        await Promise.all(
          recipients.map((rid) =>
            push(ref(database, `broadcasts/${rid}`), { ...data, status: "sent" })
          )
        );
        setEditingDraftId(null);
      } else {
        const broadcastRef = push(ref(database, `broadcasts/${currentUserId}`));
const broadcastId = broadcastRef.key;

const fullData = {
  ...data,
  id: broadcastId
};

// Save for sender
await set(ref(database, `broadcasts/${currentUserId}/${broadcastId}`), fullData);

// Save for all recipients with SAME ID
await Promise.all(
  recipients.map((rid) =>
    set(ref(database, `broadcasts/${rid}/${broadcastId}`), fullData)
  )
);
      }

      // Reset form
      setHeading("");
      setMessage("");
      setValidity("2 days");
      setSelectedUsers([]);
      setRecipientType("all");
      setEditingDraftId(null);
      showNotification(status === "sent" ? "Broadcast sent!" : "Draft saved!");
      setActiveTab(status === "sent" ? "sent" : "drafts");
    } catch (err) {
      showNotification("Operation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((u) =>
    u.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sentBroadcasts = broadcasts.filter((b) => b.status === "sent");
  const draftBroadcasts = broadcasts.filter((b) => b.status === "draft");

  return (
    <div className="broadcast-container">
      {/* Updated notification with unique class names */}
      {notification.show && (
        <div className={`broadcast-notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="broadcast-header">
        <h1>Broadcasting Dashboard</h1>
        <p>Send messages to all or selected users</p>
      </div>

      <div className="tabs">
        <button
          className={activeTab === "compose" ? "active" : ""}
          onClick={() => setActiveTab("compose")}
        >
          Compose
        </button>
        <button
          className={activeTab === "drafts" ? "active" : ""}
          onClick={() => setActiveTab("drafts")}
        >
          Drafts ({draftBroadcasts.length})
        </button>
        <button
          className={activeTab === "sent" ? "active" : ""}
          onClick={() => setActiveTab("sent")}
        >
          Sent ({sentBroadcasts.length})
        </button>
      </div>

      {/* Compose Tab */}
      {activeTab === "compose" && (
        <div className="card">
          <h2>{editingDraftId ? "Edit Draft" : "Compose New Broadcast"}</h2>

          <div className="form-grid">
            <div className="form-group">
              <label>Heading *</label>
              <input
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
                placeholder="Enter heading"
              />
            </div>

            <div className="form-group">
              <label>Message *</label>
              <textarea
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your message..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Validity</label>
                <select
                  value={validity}
                  onChange={(e) => setValidity(e.target.value)}
                >
                  <option value="1 day">1 day</option>
                  <option value="2 days">2 days</option>
                  <option value="1 week">1 week</option>
                  <option value="2 weeks">2 weeks</option>
                  <option value="1 month">1 month</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Recipients</label>
                <div className="recipient-tabs">
                  <label className="recipient-tab">
                    <input
                      type="checkbox"
                      checked={recipientType === "all"}
                      onChange={() => setRecipientType("all")}
                    />
                    <span className="tab-label">
                      All Users <span className="counter">({users.length})</span>
                    </span>
                  </label>
                  <label className="recipient-tab">
                    <input
                      type="checkbox"
                      checked={recipientType === "selected"}
                      onChange={() => setRecipientType("selected")}
                    />
                    <span className="tab-label">
                      Select Users{" "}
                      <span className="counter">({selectedUsers.length})</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {recipientType === "selected" && (
              <div className="user-selector-card">
                <div className="selector-header">
                  <input
                    type="text"
                    placeholder="Search companies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="selector-actions">
                    <button
                      onClick={() =>
                        setSelectedUsers(users.map((u) => u.userId))
                      }
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setSelectedUsers([])}
                      className="clear"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="user-list">
                  {filteredUsers.map((user) => (
                    <label key={user.userId} className="user-item">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.userId)}
                        onChange={() => handleUserSelect(user.userId)}
                      />
                      <div>
                        <div className="company">{user.company_name}</div>
                        <div className="id">
                          ID: {user.userId.slice(0, 10)}...
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="actions">
              <button
                onClick={() => saveBroadcast("sent")}
                className="send"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Broadcast"}
              </button>
              <button onClick={() => saveBroadcast("draft")} disabled={loading}>
                Save as Draft
              </button>
              {editingDraftId && (
                <button
                  onClick={() => {
                    setEditingDraftId(null);
                    setHeading("");
                    setMessage("");
                    setSelectedUsers([]);
                    setRecipientType("all");
                  }}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Drafts & Sent Tabs */}
      {(activeTab === "drafts" || activeTab === "sent") && (
        <div className="broadcasts-grid">
          {(activeTab === "drafts" ? draftBroadcasts : sentBroadcasts).map(
            (b) => (
              <div key={b.id} className="broadcast-card">
                <div className="card-header">
                  <h3>{b.heading}</h3>
                  <div className="actions">
                    {activeTab === "drafts" && (
                      <button
                        onClick={() => handleEditDraft(b)}
                        title="Edit"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                          color: "#3498db",
                        }}
                      >
                        <Pencil size={18} />
                      </button>
                    )}

                    <button
                      onClick={() =>
                        handleDelete(
                          b.id,
                          activeTab === "drafts" ? "draft" : "broadcast"
                        )
                      }
                      title="Delete"
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        color: "#e74c3c",
                      }}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <span className={`badge ${b.status}`}>
                  {b.status === "draft" ? "Draft" : "Sent"}
                </span>
                <p className="date">
                  {b.status === "draft" ? "Saved" : "Sent"}:{" "}
                  {new Date(b.timestamp).toLocaleString()}
                </p>
                <p className="message-preview">
                  {b.message.length > 120
                    ? b.message.substring(0, 120) + "..."
                    : b.message}
                </p>
                <div className="footer">
                  <span>Recipients: {b.recipients?.length || 0}</span>
                  <span>Validity: {b.validity}</span>
                </div>
              </div>
            )
          )}
          {activeTab === "drafts" && draftBroadcasts.length === 0 && (
            <div className="empty">No drafts yet</div>
          )}
          {activeTab === "sent" && sentBroadcasts.length === 0 && (
            <div className="empty">No sent broadcasts</div>
          )}
        </div>
      )}
    </div>
  );
};

export default BroadcastingPage;